import Phaser from 'phaser';
import { BaseEnemy, ALL_DIRS, Direction, GridQuery } from './BaseEnemy';

function reverseOf(d: Direction): Direction {
  switch (d) {
    case 'up': return 'down';
    case 'down': return 'up';
    case 'left': return 'right';
    case 'right': return 'left';
  }
}

/**
 * Wanders predictably (like Wanderer) until it senses a bomb in its row/col
 * within range; then flees to a perpendicular escape direction. Speed 75%.
 */
export class BombDodger extends BaseEnemy {
  private lastDecisionCell = '';
  private getBombCells: () => Array<{ col: number; row: number; range: number }>;

  constructor(
    scene: Phaser.Scene,
    col: number, row: number,
    grid: GridQuery,
    getBombCells: () => Array<{ col: number; row: number; range: number }>,
  ) {
    super(scene, 'enemy-dodger', col, row, 0.75, grid);
    this.getBombCells = getBombCells;
    this.applyDir(Phaser.Utils.Array.GetRandom(ALL_DIRS) as Direction);
  }

  update(_time: number, _delta: number) {
    if (!this.alive) return;
    this.animateWalk(_delta);
    if (!this.isCenteredOnTile(6)) return;

    const { col, row } = this.centerOfTile();
    const cellKey = `${col},${row}`;

    // Check for any bomb whose cross-line includes us
    const danger = this.detectDanger(col, row);
    if (danger) {
      // Flee perpendicular to the bomb's axis if possible
      const fleeOptions: Direction[] = danger.axis === 'col'
        ? ['left', 'right']     // bomb above/below → flee sideways
        : ['up', 'down'];        // bomb left/right → flee vertically
      const safe = fleeOptions.filter(d => this.canMoveTo(d));
      if (safe.length > 0) {
        this.applyDir(Phaser.Utils.Array.GetRandom(safe) as Direction);
        this.lastDecisionCell = cellKey;
        return;
      }
    }

    if (cellKey === this.lastDecisionCell && this.canMoveTo(this.currentDir)) return;
    this.lastDecisionCell = cellKey;

    // Wander (straight until blocked)
    if (this.canMoveTo(this.currentDir)) {
      this.applyDir(this.currentDir);
      return;
    }
    const options = ALL_DIRS.filter(d => this.canMoveTo(d));
    if (options.length === 0) {
      (this.body as Phaser.Physics.Arcade.Body).setVelocity(0, 0);
      return;
    }
    const rev = reverseOf(this.currentDir);
    const nonReverse = options.filter(d => d !== rev);
    const choice = nonReverse.length > 0 ? nonReverse : options;
    this.applyDir(Phaser.Utils.Array.GetRandom(choice) as Direction);
  }

  private detectDanger(col: number, row: number): { axis: 'col' | 'row' } | null {
    const bombs = this.getBombCells();
    for (const b of bombs) {
      if (b.col === col && Math.abs(b.row - row) <= b.range) return { axis: 'col' };
      if (b.row === row && Math.abs(b.col - col) <= b.range) return { axis: 'row' };
    }
    return null;
  }
}
